<template>
  <b-form novalidate>
    <template v-if="flowStep === 'form'">
      <b-form-group label="CPF" label-for="document">
        <b-input-group>
          <b-input
            id="document"
            v-model="form.document"
            type="search"
            placeholder="Informe CPF do cliente"
            :state="validation.document"
            :disabled="duplicateMode"
          />
          <b-input-group-append>
            <b-button
              v-if="!duplicateMode"
              variant="primary"
              :disabled="!form.document"
              @click="onSearch"
            >
              <span class="fas fa-search mr-1" /> Pesquisar
            </b-button>
          </b-input-group-append>
          <b-form-invalid-feedback v-if="$v.form.document.$invalid">
            <span v-if="!$v.form.document.required" class="d-block">
              Informe o cliente
            </span>
            <span v-if="!$v.form.document.isValidCPF" class="d-block">
              Informe um CPF validation
            </span>
          </b-form-invalid-feedback>
        </b-input-group>

        <!-- dica padrǜo -->
        <div>
          <small v-if="showSearchHint" class="text-danger">
            Clique em Pesquisar para buscar os dados do cliente
          </small>
        </div>

        <!-- AVISO DISCRETO DE REPORTS -->
        <div v-if="reportsCount > 0" class="mt-2 d-flex align-items-center">
          <i
            class="bi bi-exclamation-triangle-fill me-2"
            style="font-size: 1rem"
          ></i>
          <small>
            Este cliente possui
            <b-badge
              v-b-tooltip.hover
              variant="danger"
              class="mx-1"
              title="Quantidade de reports registrados para este cliente"
            >
              {{ reportsCount }}
            </b-badge>
            {{ reportsCount === 1 ? 'aviso registrado' : 'avisos registrados' }}
          </small>
        </div>
      </b-form-group>

      <fieldset v-if="showCustomerForm">
        <b-form-group label-for="name">
          <template #label>
            <div class="d-flex justify-content-between">
              <span> Nome </span>
              <b-checkbox
                v-if="form.id && !duplicateMode"
                v-model="form.updateCustomer"
              >
                Atualizar dados
              </b-checkbox>
            </div>
          </template>
          <b-form-input
            id="name"
            ref="name"
            v-model="form.name"
            :state="validation.name"
            :disabled="duplicateMode || !form.updateCustomer"
          />
          <b-form-invalid-feedback v-if="$v.form.name.$invalid">
            Informe o nome
          </b-form-invalid-feedback>
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-group label="Telefone" label-for="phone">
              <b-form-input
                id="phone"
                v-model="form.phone"
                :disabled="duplicateMode || !form.updateCustomer"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="E-mail" label-for="email">
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                :disabled="duplicateMode || !form.updateCustomer"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>

      <b-form-group label="Periodo" label-for="period-id">
        <PeriodSelect
          v-model="form.periodId"
          :store-id="storeId"
          :validation="$v.form.periodId"
        />
        <b-form-invalid-feedback v-if="$v.form.periodId.$invalid">
          Informe o periodo
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- NOVO: select de funcionǭria, vis��vel apenas para contas kiosk -->
      <b-form-group
        v-if="shouldHandleCommissions"
        label="Funcionaria responsavel pela venda"
        label-for="commission-user-store-id"
      >
        <b-form-select
          id="commission-user-store-id"
          v-model="form.commissionUserStoreId"
          :options="commissionUsersOptions"
          :disabled="commissionUsersLoading"
          placeholder="Selecione a funcionaria responsavel"
          :state="
            $v.form.commissionUserStoreId
              ? !$v.form.commissionUserStoreId.$invalid
              : null
          "
        />

        <b-form-invalid-feedback v-if="$v.form.commissionUserStoreId.$invalid">
          Selecione a funcionaria responsavel pela venda
        </b-form-invalid-feedback>
      </b-form-group>
    </template>

    <template v-else-if="flowStep === 'terms'">
      <div class="mb-2">
        <h5 class="mb-1">Termo para assinatura</h5>
        <small class="text-muted">
          Confirme que o cliente leu o termo. Em seguida clique em Assinar.
        </small>
      </div>

      <b-card class="mb-3" style="max-height: 50vh; overflow: auto">
        <div v-if="signatureTermsLoading" class="text-center py-3">
          Carregando termo...
        </div>
        <pre
          v-else
          class="mb-0"
          style="white-space: pre-wrap; font-family: inherit"
          >{{ signatureTermsText }}</pre
        >
      </b-card>

      <div class="d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="backToForm">
          Voltar
        </b-button>
        <b-button
          variant="primary"
          :disabled="signatureTermsLoading"
          @click="goToSignature"
        >
          Assinar
        </b-button>
      </div>
    </template>

    <template v-else>
      <div class="mb-2">
        <h5 class="mb-1">Assinatura</h5>
        <small class="text-muted">
          Assine no quadro abaixo (toque ou mouse). Use Limpar para recomeçar.
        </small>
      </div>

      <SignaturePad ref="signaturePad" :height="signaturePadHeight" />

      <div class="signature-actions d-flex align-items-center flex-nowrap mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          :disabled="submitting"
          aria-label="Voltar"
          title="Voltar"
          @click="backToTerms"
        >
          <i class="fas fa-arrow-left"></i>
          <span class="d-none d-md-inline ml-1">Voltar</span>
        </b-button>

        <b-button
          variant="outline-danger"
          class="mr-2"
          :disabled="submitting"
          aria-label="Limpar"
          title="Limpar"
          @click="clearSignaturePad"
        >
          <i class="fas fa-eraser"></i>
          <span class="d-none d-md-inline ml-1">Limpar</span>
        </b-button>

        <b-button
          variant="primary"
          class="flex-grow-1 text-truncate"
          :disabled="submitting"
          @click="confirmAndSubmit"
        >
          <i class="fas fa-check mr-1"></i>
          <span class="d-md-none">Iniciar aluguel</span>
          <span class="d-none d-md-inline">Confirmar / Iniciar aluguel</span>
        </b-button>
      </div>
    </template>
  </b-form>
</template>

<script>
import { isValidCPF } from '@/helpers/validators'
import { required } from 'vuelidate/lib/validators'
import withFormValidation from '@/mixins/withFormValidation'
import PeriodSelect from '@/components/Periods/PeriodSelect'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import { RENT_SIGNATURE_TERMS, ROLES } from '@/constants' // ajusta o caminho conforme seu projeto
import SignaturePad from '@/components/SignaturePad'

export default {
  name: 'ToyRentStartModalForm',
  components: { PeriodSelect, SignaturePad },
  mixins: [
    withFormValidation({
      // adicionamos commissionUserStoreId pra mixin cuidar do estado/dirt
      fields: ['document', 'name', 'periodId', 'commissionUserStoreId']
    }),
    withAsyncAction({
      key: 'customer',
      fetcher: {
        methodName: 'fetchCustomerByDocument',
        handler: services.customers.fetchCustomerByDocument
      }
    })
  ],
  // usamos fun��ǜo pra poder acessar this.isKiosk na valida��ǜo dinǽmica
  validations() {
    return {
      form: {
        document: { required, isValidCPF },
        name: { required },
        periodId: { required },
        // s�� Ǹ obrigat��rio quando for conta kiosk
        commissionUserStoreId: this.shouldHandleCommissions ? { required } : {}
      }
    }
  },
  props: {
    toy: { type: Object, required: true },
    submitted: { type: Boolean },
    storeId: { type: [String, Number], default: null },
    storeHasCommissions: { type: Boolean, default: null },
    storeHasRentSignature: { type: Boolean, default: null },
    duplicateMode: { type: Boolean, default: false },
    duplicateSourceRentId: { type: [String, Number], default: null },
    value: { type: Object, required: true }
  },
  data() {
    return {
      flowStep: 'form',
      submitting: false,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
      showCustomerForm: false,
      dirty: false,
      // contagem de reports encontrados
      reportsCount: 0,

      // op����es ser��o carregadas do backend (/user/commissionable)
      commissionUsersOptions: [],
      commissionUsersLoading: false,
      signatureTermsLoading: false,
      signatureTermsValue: RENT_SIGNATURE_TERMS
    }
  },
  computed: {
    form: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    showSearchHint() {
      const { $dirty, $invalid } = this.$v.form
      return $dirty && !$invalid && !this.showCustomerForm
    },
    isKiosk() {
      const isKiosk = this.$store.getters['store/isKiosk']
      const isAdmin = this.$store.getters['store/isAdmin']
      return isAdmin || isKiosk
    },
    hasCommissions() {
      if (typeof this.storeHasCommissions === 'boolean') {
        return this.storeHasCommissions
      }

      // Se hÁ uma loja selecionada, evitamos cair na loja global atÁ saber o flag.
      if (this.storeHasCommissions === null && this.storeId) {
        return false
      }

      return this.$store.getters['store/hasCommissions']
    },
    hasRentSignature() {
      if (typeof this.storeHasRentSignature === 'boolean') {
        return this.storeHasRentSignature
      }

      // Se ha uma loja selecionada, evitamos cair na loja global ate saber o flag.
      if (this.storeHasRentSignature === null && this.storeId) {
        return false
      }

      return this.$store.getters['store/hasRentSignature']
    },
    shouldCollectSignature() {
      return this.hasRentSignature && !this.duplicateMode
    },
    shouldHandleCommissions() {
      return this.hasCommissions && this.isKiosk
    },
    signatureTermsText() {
      return this.signatureTermsValue
    },
    signaturePadHeight() {
      const base = Math.floor(this.viewportHeight * 0.55)
      return Math.max(260, Math.min(520, base))
    }
  },
  watch: {
    storeId(newVal) {
      this.form = {
        ...this.form,
        commissionUserStoreId: null
      }

      if (this.shouldHandleCommissions && newVal) {
        this.fetchCommissionUsers()
      }
    },
    shouldHandleCommissions(newVal) {
      if (newVal) {
        if (this.storeId) {
          this.fetchCommissionUsers()
        }
      } else {
        this.commissionUsersOptions = []
        this.form = {
          ...this.form,
          commissionUserStoreId: null
        }
      }
    }
  },
  mounted() {
    this.$emit('flow-step-change', this.flowStep)
    window.addEventListener('resize', this.onViewportResize, { passive: true })

    if (typeof this.form.commissionUserStoreId === 'undefined') {
      this.form = {
        ...this.form,
        commissionUserStoreId: null
      }
    }

    if (typeof this.form.signature === 'undefined') {
      this.form = {
        ...this.form,
        signature: null
      }
    }

    if (this.shouldHandleCommissions && this.storeId) {
      this.fetchCommissionUsers()
    }

    if (this.duplicateMode) {
      this.showCustomerForm = true
      this.form = {
        ...this.form,
        updateCustomer: false,
        duplicateSourceRentId: this.duplicateSourceRentId
      }
    } else if (this.form.document) {
      this.onSearch()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportResize)
  },
  methods: {
    onViewportResize() {
      this.viewportHeight = window.innerHeight
    },
    setFlowStep(step) {
      this.flowStep = step
      this.$emit('flow-step-change', step)
    },
    handleSubmit() {
      if (this.flowStep !== 'form') return

      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$root.$toast?.error('Preencha todos os campos obrigatorios.')
        return
      }

      if (!this.shouldCollectSignature) {
        this.submitWithoutSignature()
        return
      }

      this.goToTerms()
    },
    backToForm() {
      this.submitting = false
      this.setFlowStep('form')
    },
    goToTerms() {
      this.submitting = false
      this.signatureTermsLoading = true
      this.setFlowStep('terms')
      this.loadSignatureTerms()
    },
    async loadSignatureTerms() {
      const storeId = Number(this.storeId)
      if (!storeId) {
        this.signatureTermsValue = RENT_SIGNATURE_TERMS
        this.signatureTermsLoading = false
        return
      }

      try {
        const result = await services.stores.fetchRentTerms(storeId)
        const text = result?.rentTermText
        this.signatureTermsValue =
          typeof text === 'string' && text.trim()
            ? text.trim()
            : RENT_SIGNATURE_TERMS
      } catch (e) {
        this.signatureTermsValue = RENT_SIGNATURE_TERMS
        this.$root.$toast?.warning(
          'Não foi possível carregar o termo da loja. Usando termo padrão.'
        )
      } finally {
        this.signatureTermsLoading = false
      }
    },
    submitWithoutSignature() {
      const nextForm = {
        ...this.form,
        signature: null
      }

      this.form = nextForm
      this.submitting = true
      this.$emit('submit', nextForm)
      window.setTimeout(() => {
        if (this.flowStep === 'form') this.submitting = false
      }, 8000)
    },
    clearSignaturePad() {
      this.$refs.signaturePad?.clear?.()
      this.form = {
        ...this.form,
        signature: null
      }
    },
    goToSignature() {
      this.submitting = false
      this.setFlowStep('sign')
      this.$nextTick(() => {
        this.$refs.signaturePad?.resizeCanvas?.()
      })
    },
    backToTerms() {
      this.submitting = false
      this.setFlowStep('terms')
    },
    confirmAndSubmit() {
      const pad = this.$refs.signaturePad
      if (!pad || pad.isEmpty()) {
        this.$root.$toast?.error('Assinatura vazia. Assine antes de confirmar.')
        return
      }

      const signature = {
        termAccept: this.signatureTermsText,
        signatureSvg: pad.getSvg(),
        strokesJson: pad.getStrokesJson(),
        meta: pad.getMeta()
      }

      const nextForm = {
        ...this.form,
        signature
      }

      this.form = nextForm
      this.submitting = true
      this.$emit('submit', nextForm)
      window.setTimeout(() => {
        if (this.flowStep === 'sign') this.submitting = false
      }, 8000)
    },
    roleLabel(role) {
      const entry = Object.values(ROLES).find((r) => r.value === role)
      return entry ? entry.text : role
    },

    async fetchCommissionUsers() {
      if (!this.shouldHandleCommissions) return

      this.commissionUsersLoading = true
      this.commissionUsersOptions = []

      try {
        const response = await services.users.fetchCommissionableUsers(
          this.storeId
        )
        const list = (response || []).filter(
          ({ role }) => role === 'employer' || role === 'admin'
        )

        this.commissionUsersOptions = list.map((user) => ({
          value: user.userStoreId,
          text: user.role
            ? `${user.name} (${this.roleLabel(user.role)})`
            : user.name,
          role: user.role
        }))
      } catch (error) {
        console.log('Error fetching commissionable users:', error)
        this.commissionUsersOptions = []
        if (
          error?.response?.status === 400 ||
          error?.response?.status === 401
        ) {
          this.$root.$toast?.error(
            'Nao foi possivel carregar as funcionarias para comissao. Verifique a loja e sua sessao.'
          )
        } else {
          this.$root.$toast?.error(
            'Erro ao carregar funcionarias para comissao.'
          )
        }
      } finally {
        this.commissionUsersLoading = false
      }
    },
    async onSearch() {
      this.$v.form.document.$touch()

      if (!this.$v.form.document.$invalid) {
        try {
          // 1) busca cliente
          const customer = await this.fetchCustomerByDocument(
            this.form.document
          )
          this.updateForm(customer)

          // 2) discretamente busca reports por customerId e atualiza badge
          if (customer?.id) {
            try {
              const { data } =
                await services.customers.fetchCustomerReportsByCustomerId(
                  customer.id
                )
              const list = Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                ? data
                : []
              this.reportsCount = list.length || 0
            } catch (err) {
              // silencioso; nǜo quebra o fluxo de aluguel
              this.reportsCount = 0
            }
          } else {
            this.reportsCount = 0
          }
        } catch (error) {
          if (error?.response?.status === 400) {
            this.form.updateCustomer = true
            setTimeout(() => this.$refs.name.$el.focus(), 100)
            this.$root.$toast.warning(
              'Cliente nǜo encontrado. Preencha os dados para criar um novo cliente'
            )
            this.updateForm()
            this.reportsCount = 0
          }
        } finally {
          this.showCustomerForm = true
        }
      }
    },
    updateForm(customer) {
      this.form = {
        ...this.form,
        id: customer?.id ?? '',
        name: customer?.name ?? '',
        phone: customer?.phone ?? '',
        email: customer?.email ?? ''
      }
    }
  }
}
</script>
